import { Injectable } from '@angular/core';

import { adhocService } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';

import { Client } from './model/objects/client';
import { PlaceView } from './model/views/place';
import { TicketView } from './model/views/ticket';

/************************************/
/* PUBLIC CONTRACT                  */
/************************************/

/**
 * Maximum ticket list rows kept in RAM per place.
 * @see RN-TLC-05
 */
export const TICKET_LIST_CACHE_MAX = 10000;

/**
 * Lightweight row aligned with `place/ticketlst` (`Ticketlst.php`).
 * Extra server fields are preserved via the index signature.
 */
export type TicketInfo = {
    objid: string;
    created: string | null;
    updated: string | null;
    series?: string | null;
    invceno?: string | null;
    status?: string | null;
    payment?: string | null;
    tid?: string | null;    // Till objid from `place/ticketlst`; display label stays in `till`
    price?: number | null;
    account?: string | null;
    invceac?: string | null;
} & Record<string, unknown>;

/************************************/
/* VALUE CODEC                      */
/************************************/

/**
 * Parses and formats ticket list row date/time fields shared by API rows and live projections.
 */
class TicketListValueCodec {

    /**
     * Coerces an unknown value to a trimmed string or `null` when empty.
     *
     * @param value - Raw field from API or live projection.
     * @returns Normalized string or `null`.
     */
    static NullableString(value: unknown): string | null {
        if (value === null || value === undefined){
            return null;
        }

        const text = String(value).trim();
        return text === '' ? null : text;
    }

    /**
     * Coerces an unknown value to a finite number or `null`.
     *
     * @param value - Raw field from API or live projection.
     * @returns Parsed number or `null`.
     */
    static NullableNumber(value: unknown): number | null {
        if (value === null || value === undefined || value === ''){
            return null;
        }

        const num = Number(value);
        return Number.isFinite(num) ? num : null;
    }

    /**
     * Reads a `created` or `updated` field from a list entry as epoch milliseconds.
     *
     * @param entry - Cached or projected row.
     * @param field - Timestamp column to read.
     * @returns Epoch ms or `null` when the field is missing or invalid.
     */
    static FieldEpochMs(entry: TicketInfo, field: 'created' | 'updated'): number | null {
        return TicketListValueCodec.ParseEpochMs(entry[field]);
    }

    /**
     * Epoch ms used for cache eviction: `updated`, then `created` when missing.
     *
     * @param entry - Cached or projected row.
     * @returns Epoch ms for LRU-style eviction ordering.
     */
    static EvictionEpochMs(entry: TicketInfo): number {
        return TicketListValueCodec.FieldEpochMs(entry, 'updated')
            ?? TicketListValueCodec.FieldEpochMs(entry, 'created')
            ?? 0;
    }

    /**
     * Parses a date-like value to epoch milliseconds.
     * Accepts `Date`, epoch seconds, epoch milliseconds, or parseable strings.
     *
     * @param value - Raw timestamp from API or live model.
     * @returns Epoch ms or `null` when parsing fails.
     */
    static ParseEpochMs(value: unknown): number | null {
        if (value instanceof Date){
            return value.getTime();
        }

        if (value === null || value === undefined || value === ''){
            return null;
        }

        if (typeof value === 'number'){
            return value < 1e12 ? value * 1000 : value;
        }

        const parsed = new Date(String(value));
        return Number.isFinite(parsed.getTime()) ? parsed.getTime() : null;
    }

    /**
     * Formats a `Date` as `YYYY-MM-DD HH:mm:ss`, matching `place/ticketlst` row strings.
     *
     * @param date - Live ticket timestamp.
     * @returns Formatted date/time string.
     */
    static FormatDateTime(date: Date): string {
        const pad = (part: number) => String(part).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} `
            + `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    }

    /**
     * Local calendar day start (00:00:00.000).
     */
    static StartOfDay(date: Date): Date {
        const normalized = new Date(date.getTime());
        normalized.setHours(0, 0, 0, 0);
        return normalized;
    }

    /**
     * First instant of the calendar day after {@link date}'s local day (RN-TLC-16 HTTP upper bound).
     */
    static StartOfNextDay(date: Date): Date {
        const next = TicketListValueCodec.StartOfDay(date);
        next.setDate(next.getDate() + 1);
        return next;
    }

    /**
     * Calendar-day bounds used for in-flight sync dedup and HTTP period (RN-TLC-16).
     * Response filtering still uses the caller's original {@link ini} / {@link end}.
     */
    static SyncPeriodBounds(ini: Date, end: Date): { ini: Date; end: Date } {
        const syncIni = TicketListValueCodec.StartOfDay(ini);
        const endDayStart = TicketListValueCodec.StartOfDay(end);
        const syncEnd = (end.getTime() === endDayStart.getTime())
            ? end
            : TicketListValueCodec.StartOfNextDay(end);

        return { ini: syncIni, end: syncEnd };
    }
}

/************************************/
/* ROW MAPPING                      */
/************************************/

/**
 * Normalizes API payloads and projects live {@link TicketView} rows into {@link TicketInfo}.
 */
class TicketListRowMapper {

    /**
     * Converts a raw `place/ticketlst` row into a canonical {@link TicketInfo}.
     * Unknown columns from the server are copied onto the entry.
     *
     * @param raw - Single element from the API `tickets` array.
     * @returns Normalized row or `null` when `objid` is missing.
     */
    static NormalizeRow(raw: unknown): TicketInfo | null {
        if (raw == null || typeof raw !== 'object'){
            return null;
        }

        const row = raw as Record<string, unknown>;
        const objid = String(row['objid'] ?? '').trim();
        if (objid === ''){
            return null;
        }

        const entry: TicketInfo = {
            objid,
            created: TicketListValueCodec.NullableString(row['created']),
            updated: TicketListValueCodec.NullableString(row['updated']),
            status: TicketListValueCodec.NullableString(row['status']),
            payment: TicketListValueCodec.NullableString(row['payment']),
            price: TicketListValueCodec.NullableNumber(row['price']),
            account: TicketListValueCodec.NullableString(row['account']),
            invceac: TicketListValueCodec.NullableString(row['invceac']),
            series: TicketListValueCodec.NullableString(row['series']),
            invceno: TicketListValueCodec.NullableString(row['invceno']),
        };

        for (const [key, value] of Object.entries(row)){
            if (!(key in entry)){
                entry[key] = value;
            }
        }

        return entry;
    }

    /**
     * Resolves the business client linked to a live ticket (invoice, account invoice or pay account).
     */
    static ResolveClient(ticket: TicketView): Client | null {
        const invoiceClient = ticket.invoice?.client ?? null;
        if (invoiceClient != null) {
            return invoiceClient;
        }

        const invceac = ticket.invceac as { account?: { client?: Client | null } | null } | null;
        const accountClient = invceac?.account?.client ?? null;
        if (accountClient != null) {
            return accountClient;
        }

        const payAccountClient = ticket.account?.client as Client | null | undefined;
        if (payAccountClient != null) {
            return payAccountClient;
        }

        return null;
    }

    /**
     * Projects a valid in-memory {@link TicketView} into list-entry shape for cache merge.
     *
     * @param ticket - Live ticket from {@link PlaceView.tickets}.
     * @returns Projected list row for cache merge.
     */
    static Ticket2Info(ticket: TicketView): TicketInfo {
        const _entry: TicketInfo = {
            objid: ticket.objid,
            orderno: ticket.orderno ?? ticket.objid,
            created: ticket.created ? TicketListValueCodec.FormatDateTime(ticket.created) : null,
            updated: ticket.updated ? TicketListValueCodec.FormatDateTime(ticket.updated) : null,
            status: ticket.status ?? null,
            payment: ticket.payment ?? null,
            price: ticket.price ?? ticket.priceinfo?.tax?.total ?? ticket.priceinfo?.val ?? null,
            taxes: ticket.taxes ?? ticket.priceinfo?.tax?.taxes ?? null,
            taxdetail: TicketListRowMapper.TaxdetailJson(ticket),
            account: ticket.account?.objid ?? null,
            invceac: ticket.invceac?.objid ?? null,
            series: ticket.series ?? null,
            invceno: ticket.invceno ?? null,
            tid: ticket.till?.objid ?? null,
            paidon: ticket.paidon ? TicketListValueCodec.FormatDateTime(ticket.paidon) : null,
        };

        const businessClient = TicketListRowMapper.ResolveClient(ticket);
        if (businessClient != null) {
            _entry['client'] = TicketListValueCodec.NullableString(businessClient.objid);
            _entry['clientname'] = TicketListValueCodec.NullableString(businessClient.name);
            _entry['clienttaxid'] = TicketListValueCodec.NullableString(businessClient.taxid);
        }

        if (ticket.payment === 'PAYMIXED'){
            let _cash = 0;
            let _card = 0;

            for (const _mixed of ticket.mixed){
                if (_mixed.payment === 'PAYCASH'){
                    _cash += Number(_mixed.amount) || 0;
                }
                else if (_mixed.payment === 'PAYCARD'){
                    _card += Number(_mixed.amount) || 0;
                }
            }

            _entry['paycash'] = _cash;
            _entry['paycard'] = _card;
        }

        return _entry;
    }

    /**
     * Builds ticket-list taxdetail JSON from the live ticket price split.
     */
    private static TaxdetailJson(ticket: TicketView): string | null {
        const split = ticket.priceinfo?.tax?.split;
        if (!Array.isArray(split) || split.length === 0){
            return null;
        }

        const rows: Array<{ rate: number; amount: number; base: number }> = [];
        for (const entry of split){
            const amount = Number(entry.taxes);
            if (!Number.isFinite(amount) || amount === 0){
                continue;
            }

            rows.push({
                rate: Number(entry.rate),
                amount,
                base: Number(entry.base),
            });
        }

        return rows.length > 0 ? JSON.stringify(rows) : null;
    }

    /**
     * Overlays live ticket fields while keeping API tax metadata when live projection lacks it.
     */
    static MergeLiveRow(existing: TicketInfo | undefined, live: TicketInfo): TicketInfo {
        if (!existing){
            return live;
        }

        const merged: TicketInfo = { ...existing, ...live };
        const liveTaxes = TicketListValueCodec.NullableNumber(live['taxes']);
        const existingTaxes = TicketListValueCodec.NullableNumber(existing['taxes']);
        if ((liveTaxes === null || liveTaxes === 0) && existingTaxes !== null && existingTaxes > 0){
            merged['taxes'] = existingTaxes;
        }

        const liveTaxdetail = TicketListValueCodec.NullableString(live['taxdetail']);
        const existingTaxdetail = TicketListValueCodec.NullableString(existing['taxdetail']);
        const liveTaxdetailEmpty = liveTaxdetail === null || liveTaxdetail === '[]';
        const existingTaxdetailPresent = existingTaxdetail !== null && existingTaxdetail !== '[]';
        if (liveTaxdetailEmpty && existingTaxdetailPresent){
            merged['taxdetail'] = existingTaxdetail;
        }

        return merged;
    }
}

/************************************/
/* PER-PLACE RAM CACHE              */
/************************************/

/**
 * In-memory ticket list cache for one place.
 * Holds merged API and live rows plus incremental-sync watermarks.
 */
class TicketListCache {

    /** Rows keyed by ticket `objid`. Live merge overwrites stale API rows (RN-TLC-11). */
    readonly byId = new Map<string, TicketInfo>();

    /** Last `place/ticketlst` response timestamp (shared; server always matches CREATED OR UPDATED). */
    lastTimestamp = 0;

    /** Earliest `dateini` (epoch ms) covered by a full-period fetch (RN-TLC-01). */
    minPeriodIni = Number.MAX_SAFE_INTEGER;

    /** Earliest `created` epoch ms among cached rows (live merge included). */
    minRowCreated = Number.MAX_SAFE_INTEGER;

    /**
     * Merges normalized or raw rows into the cache.
     *
     * @param rows - API `tickets` payload or pre-normalized entries.
     */
    mergeRows(rows: unknown[]): void {
        for (const raw of rows){
            const entry = TicketListRowMapper.NormalizeRow(raw);
            if (entry){
                this.byId.set(entry.objid, entry);
            }
        }

        this.recalcRowBounds();
    }

    /**
     * Merges already-normalized entries without re-parsing.
     *
     * @param entries - Projected live rows.
     */
    mergeEntries(entries: TicketInfo[]): void {
        for (const entry of entries){
            const existing = this.byId.get(entry.objid);
            this.byId.set(entry.objid, TicketListRowMapper.MergeLiveRow(existing, entry));
        }

        this.recalcRowBounds();
    }

    /**
     * Recomputes {@link minRowCreated} from {@link byId}.
     */
    recalcRowBounds(): void {
        let minRowCreated = Number.MAX_SAFE_INTEGER;

        for (const entry of this.byId.values()){
            const createdMs = TicketListValueCodec.FieldEpochMs(entry, 'created');
            if (createdMs == null){
                continue;
            }

            if (createdMs < minRowCreated){
                minRowCreated = createdMs;
            }
        }

        if (this.byId.size === 0){
            this.minRowCreated = Number.MAX_SAFE_INTEGER;
            return;
        }

        this.minRowCreated = minRowCreated;
    }

    /**
     * Earliest period start the cache can satisfy without a full refetch.
     * Based on the oldest row still stored — not on a prior full-fetch watermark alone (RN-TLC-01).
     */
    coverageIni(): number {
        return this.minRowCreated;
    }

    /**
     * Drops stalest rows by `updated` (fallback `created`) until within {@link TICKET_LIST_CACHE_MAX}.
     *
     * @see RN-TLC-05
     */
    evictToLimit(): void {
        if (this.byId.size <= TICKET_LIST_CACHE_MAX){
            return;
        }

        const sizeBefore = this.byId.size;
        const sorted = Array.from(this.byId.entries()).sort((left, right) => {
            return TicketListValueCodec.EvictionEpochMs(left[1])
                - TicketListValueCodec.EvictionEpochMs(right[1]);
        });

        while (this.byId.size > TICKET_LIST_CACHE_MAX){
            const oldest = sorted.shift();
            if (!oldest){
                break;
            }
            this.byId.delete(oldest[0]);
        }

        this.recalcRowBounds();

        if (this.byId.size < sizeBefore && this.byId.size > 0){
            this.minPeriodIni = this.minRowCreated;
        }
    }

    /**
     * Returns cached rows whose selected timestamp falls within the query period.
     *
     * @param ini - Period start (inclusive).
     * @param end - Period end (inclusive).
     * @param mode - Field used for filtering (`CREATED` or `UPDATED`).
     * @returns Rows matching the period; does not mutate the cache.
     */
    filterByPeriod(ini: Date, end: Date, mode: 'CREATED' | 'UPDATED'): TicketInfo[] {
        return TicketListCache.filterRowsByPeriod(this.byId.values(), ini, end, mode);
    }

    /**
     * Filters row collections by period and response mode.
     *
     * @param rows - Candidate rows (full query snapshot or cache values).
     * @param ini - Period start (inclusive).
     * @param end - Period end (inclusive).
     * @param mode - Field used for filtering (`CREATED` or `UPDATED`).
     */
    static filterRowsByPeriod(
        rows: Iterable<TicketInfo>,
        ini: Date,
        end: Date,
        mode: 'CREATED' | 'UPDATED',
    ): TicketInfo[] {
        const iniMs = ini.getTime();
        const endMs = end.getTime();
        const field = mode === 'UPDATED' ? 'updated' : 'created';

        return Array.from(rows).filter((entry) => {
            const ms = TicketListValueCodec.FieldEpochMs(entry, field);
            return ms !== null && ms >= iniMs && ms <= endMs;
        });
    }
}

/************************************/
/* TICKET LIST SERVICE              */
/************************************/

/**
 * Self-managed historical ticket queries via `GET place/ticketlst`.
 * {@link Query} is the sole public entry point: it resolves cache hit/miss, incremental sync,
 * live TPV merge, and eviction internally. Callers supply {@link PlaceView}, period, and mode only.
 * Requires an online connection; callers must guard with `view.IsOnline`.
 * Features must not call `adhoc.DoRequest('place/ticketlst', …)` directly.
 *
 * @see RN-TLC-01 … RN-TLC-14 in `src/frontlib/tickets.mdc`
 */
@Injectable({
    providedIn: 'root',
})
export class ticketService {

    private _cache: TicketListCache | null = null;
    private _cachePlace: PlaceView | null = null;

    /** In-flight cache sync per place + calendar-day period; resolves pre-evict snapshot (RN-TLC-16). */
    private _syncInflight = new Map<string, Promise<Map<string, TicketInfo>>>();

    /**
     * @param adhoc - HTTP adapter for `place/ticketlst`.
     * @param view - Online/offline gate for {@link Query}.
     */
    constructor(
        private readonly adhoc: adhocService,
        private readonly view: viewService,
    ) {}

    /********************************/
    /* PUBLIC API                   */
    /********************************/

    /**
     * Returns historical ticket rows for a period.
     * Cache lifecycle (live merge, hit/miss, incremental fetch, eviction) is handled internally.
     * Callers must not invoke this method while offline.
     *
     * @param place - {@link PlaceView} supplied by the caller.
     * @param ini - Period start (inclusive).
     * @param end - Period end (inclusive).
     * @param mode - `'CREATED'` (reports) or `'UPDATED'` (accounts); filters the response only.
     * @returns Rows whose `created` or `updated` timestamp falls within the period for the given mode.
     *          The current response includes every row from the fetch/live merge; RAM cache is capped separately (`RN-TLC-05`).
     * @throws When offline (RN-TLC-14).
     */
    async Query(place: PlaceView, ini: Date, end: Date, mode: 'CREATED' | 'UPDATED' = 'CREATED'): Promise<TicketInfo[]> {
        if (!this.view.IsOnline){
            throw new Error('[ticketService] Query requires an online connection');
        }

        const cache = this._EnsureCache(place);

        this._MergeLiveTickets(cache, place);

        const snapshot = await this._EnsureCacheSynced(cache, place, ini, end);
        const responseMap = this._ApplyLiveTickets(snapshot, place);

        return TicketListCache.filterRowsByPeriod(responseMap.values(), ini, end, mode);
    }

    /********************************/
    /* CACHE LIFECYCLE              */
    /********************************/

    /**
     * Returns the RAM cache for the requested place.
     * Only one place is cached at a time; a different {@link PlaceView.objid} replaces the previous cache.
     *
     * @param place - Caller-supplied place view.
     */
    private _EnsureCache(place: PlaceView): TicketListCache {
        if (this._cache && this._cachePlace?.objid === place.objid){
            return this._cache;
        }

        this._cache = new TicketListCache();
        this._cachePlace = place;
        return this._cache;
    }

    /**
     * Dedupes concurrent sync for the same place and calendar-day period (RN-TLC-16).
     * The dedup key uses {@link TicketListValueCodec.SyncPeriodBounds}, not raw epoch ms.
     * Returns a pre-eviction snapshot shared by every waiter on the same in-flight sync.
     *
     * @param cache - Target place cache.
     * @param place - Caller-supplied place view.
     * @param ini - Period start (caller bounds; response filter uses these values).
     * @param end - Period end (caller bounds; response filter uses these values).
     */
    private _EnsureCacheSynced(
        cache: TicketListCache,
        place: PlaceView,
        ini: Date,
        end: Date,
    ): Promise<Map<string, TicketInfo>> {
        const syncPeriod = TicketListValueCodec.SyncPeriodBounds(ini, end);
        const key = `${place.objid}:${syncPeriod.ini.getTime()}:${syncPeriod.end.getTime()}`;
        let inflight = this._syncInflight.get(key);
        if (!inflight){
            inflight = (async () => {
                await this._SyncCache(cache, place, ini, syncPeriod.ini, syncPeriod.end);
                this._MergeLiveTickets(cache, place);
                const snapshot = new Map(cache.byId);
                cache.evictToLimit();
                return snapshot;
            })().finally(() => {
                this._syncInflight.delete(key);
            });
            this._syncInflight.set(key, inflight);
        }

        return inflight;
    }

    /**
     * Overlays live TPV rows onto a query snapshot (live wins over stale API rows).
     *
     * @param snapshot - Pre-eviction rows for this sync cycle.
     * @param place - Caller-supplied place view.
     */
    private _ApplyLiveTickets(snapshot: Map<string, TicketInfo>, place: PlaceView): Map<string, TicketInfo> {
        const responseMap = new Map(snapshot);
        for (const ticket of place.tickets){
            const live = TicketListRowMapper.Ticket2Info(ticket);
            const existing = responseMap.get(ticket.objid);
            responseMap.set(ticket.objid, TicketListRowMapper.MergeLiveRow(existing, live));
        }

        return responseMap;
    }

    /**
     * Resolves hit/miss and runs full or incremental API fetch.
     * Miss uses the caller {@link coverageIni} against {@link coverageIni}; HTTP uses {@link syncIni} / {@link syncEnd}.
     *
     * @param cache - Target place cache.
     * @param place - Caller-supplied place view.
     * @param coverageIni - Caller period start for hit/miss (RN-TLC-01).
     * @param syncIni - Calendar-day start sent to the API.
     * @param syncEnd - Calendar-day end sent to the API.
     */
    private async _SyncCache(
        cache: TicketListCache,
        place: PlaceView,
        coverageIni: Date,
        syncIni: Date,
        syncEnd: Date,
    ): Promise<void> {
        const miss = (cache.byId.size === 0) || (coverageIni.getTime() < cache.coverageIni());
        if (miss){
            await this._FetchFullPeriod(cache, place, syncIni, syncEnd);
        }
        else {
            await this._SyncIncremental(cache, place, syncIni, syncEnd);
        }
    }

    /********************************/
    /* LIVE TICKET MERGE            */
    /********************************/

    /**
     * Overlays valid in-memory tickets from the active place onto the cache (RN-TLC-11).
     *
     * @param cache - Target place cache.
     * @param place - Caller-supplied place view.
     */
    private _MergeLiveTickets(cache: TicketListCache, place: PlaceView): void {
        const rows: TicketInfo[] = [];
        for (const ticket of place.tickets){
            if (!ticket.IsValid){
                continue;
            }

            rows.push(TicketListRowMapper.Ticket2Info(ticket));
        }

        cache.mergeEntries(rows);
    }

    /********************************/
    /* API FETCH                    */
    /********************************/

    /**
     * Full-period fetch on cache miss (`timestamp: 0`, RN-TLC-02).
     * Server matches rows where `created` OR `updated` falls within the period.
     *
     * @param cache - Target place cache.
     * @param place - Place view sent to the API.
     * @param dateini - Requested period start.
     * @param dateend - Requested period end.
     * @throws When `errorcode !== 0`.
     */
    private async _FetchFullPeriod(
        cache: TicketListCache,
        place: PlaceView,
        dateini: Date,
        dateend: Date,
    ): Promise<void> {
        const data: any = await this.adhoc.DoRequest('place/ticketlst', {
            place: place.objid,
            timestamp: 0,
            dateini: Math.floor(dateini.getTime() / 1000),
            dateend: Math.floor(dateend.getTime() / 1000),
        });

        if (data['errorcode'] !== 0){
            throw new Error(`place/ticketlst errorcode ${data['errorcode']}`);
        }

        cache.mergeRows((data['tickets'] ?? []) as unknown[]);
        cache.lastTimestamp = Number(data['timestamp'] ?? cache.lastTimestamp ?? 0);
        cache.minPeriodIni = Math.min(cache.minPeriodIni, dateini.getTime());
    }

    /**
     * Incremental fetch using the stored watermark on cache hit (RN-TLC-03).
     *
     * @param cache - Target place cache.
     * @param place - Place view sent to the API.
     * @param dateini - Requested period start.
     * @param dateend - Requested period end.
     * @throws When `errorcode !== 0`.
     */
    private async _SyncIncremental(
        cache: TicketListCache,
        place: PlaceView,
        dateini: Date,
        dateend: Date,
    ): Promise<void> {
        const timestamp = cache.lastTimestamp ?? 0;
        if (timestamp <= 0){
            return;
        }

        const data: any = await this.adhoc.DoRequest('place/ticketlst', {
            place: place.objid,
            timestamp,
            dateini: Math.floor(dateini.getTime() / 1000),
            dateend: Math.floor(dateend.getTime() / 1000),
        });

        if (data['errorcode'] !== 0){
            throw new Error(`place/ticketlst errorcode ${data['errorcode']}`);
        }

        cache.mergeRows((data['tickets'] ?? []) as unknown[]);
        cache.lastTimestamp = Number(data['timestamp'] ?? timestamp);
    }
}
